import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Alert } from "@repo/ui";
import AnimatedCard from "../../../../components/AnimatedCard/AnimatedCard";
import PageContainer from "../../../../components/PageContainer/PageContainer";
import StepIndicator from "../../../../components/StepIndicator/StepIndicator";
import withAuth from "../../../../hoc/withAuth/withAuth";
import { getWithdrawSteps } from "../../constants/steps";
import { gsap } from "gsap";
import BackButton from "../../../../components/BackButton";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { startConnectOnboarding } from "../../../../services/stripeApiService";
import LoadingButton from "../../../../components/LoadingButton/LoadingButton";

const LOCALE = "modules_MyGiftTable_pages_WithdrawPage_WithdrawPageStep3";

const WithdrawPageStep3: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const steps = getWithdrawSteps(t);
  const navigate = useNavigate();
  const alertRef = useRef<HTMLDivElement>(null);
  const [alert, setAlert] = useState<{
    message: React.ReactNode;
    level: "info" | "warn" | "error" | "success";
  } | null>(null);
  const [progress, setProgress] = useState<number>(0);

  // Retrieve the current user id and email from Redux state.
  const user = useSelector((state: RootState) => state.auth.user);
  const userId = user?.id;
  const email = user?.email || "";

  useEffect(() => {
    if (alertRef.current) {
      gsap.fromTo(
        alertRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.5 }
      );
    }
  }, [alert]);

  const handleConnectStripe = async () => {
    if (!userId) {
      setAlert({
        message: t("userNotFound", "User not found."),
        level: "error",
      });
      return;
    }
    if (!email) {
      setAlert({
        message: t("emailRequired", "Email is required for Stripe onboarding."),
        level: "warn",
      });
      return;
    }
    try {
      // Set some progress while the request is processing.
      setProgress(50);
      // Call the API to initiate Stripe Connect Onboarding,
      // passing user_id, the auth user email, and hard-coded country "MX".
      const { url } = await startConnectOnboarding({
        user_id: userId,
        email,
        country: "MX",
      });
      setProgress(100);
      // Redirect the user to the Stripe-hosted onboarding page.
      window.location.href = url;
    } catch (error) {
      console.error("Error starting Stripe Connect Onboarding", error);
      setAlert({
        message: t(
          "errorStartingOnboarding",
          "Failed to start Stripe Connect onboarding."
        ),
        level: "error",
      });
      // Reset progress on error.
      setProgress(0);
    }
  };

  const handleBack = () => {
    navigate(`/my-table/withdraw/step2`);
  };

  return (
    <PageContainer>
      <div className="mt-16" />
      <StepIndicator steps={steps} currentStep={3} progressPercentage={75} />
      <AnimatedCard className="bg-white max-w-lg w-full">
        <BackButton text={t("backButton", "Back")} onClick={handleBack} />
        <h2 className="text-2xl font-bold mb-4 text-primary">
          {t("connectStripeTitle", "Connect with Stripe for Payouts")}
        </h2>
        {alert && (
          <div ref={alertRef}>
            <Alert message={alert.message} level={alert.level} />
          </div>
        )}
        <p className="text-gray-600 mb-4">
          {t(
            "connectStripeInstruction",
            "To receive funds from your gift table, please complete our secure Stripe onboarding. This process will only ask for the required details directly on Stripe."
          )}
        </p>
        <LoadingButton
          label={t("connectStripeButton", "Connect with Stripe")}
          onClick={handleConnectStripe}
          progress={progress}
          type="primary"
          className="w-full mt-6"
          disabled={progress > 0 && progress < 100}
        />
      </AnimatedCard>
    </PageContainer>
  );
};

export default withAuth(withTranslations(LOCALE)(WithdrawPageStep3));
